import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShieldCheck, Coins, Gavel, CalendarCheck, ChartLineUp, Robot,
  ArrowRight, CheckCircle, DownloadSimple,
} from "@phosphor-icons/react";

const PAINS = [
  {
    icon: Coins,
    color: "#FFB020",
    pain: "“Am I about to lose money I can’t afford to lose?”",
    heading: "Save money — and protect every grant euro.",
    body: "Track every euro against budget, pace grant spend in real time, and catch overspend before it becomes a clawback. No more wasted spend or nasty surprises at report time.",
    module: "Grant Guardian · Cash Runway",
  },
  {
    icon: Gavel,
    color: "#E63946",
    pain: "“Could I get hit with fines or legal bills?”",
    heading: "Avoid unnecessary fines & legal costs.",
    body: "Late filings and missed obligations mean penalties, interest and expensive clean-ups. PerCen360 keeps you ahead of every requirement so you stay penalty-free.",
    module: "IE + UK compliance engine",
  },
  {
    icon: CalendarCheck,
    color: "#00A86B",
    pain: "“Will I make the deadline?”",
    heading: "Meet every deadline, effortlessly.",
    body: "A single calendar for CRO, Corporation Tax, VAT and RBO across Ireland and the UK — dated, prioritised and impossible to forget.",
    module: "Multi-jurisdiction deadlines",
  },
  {
    icon: ChartLineUp,
    color: "#7AE1B3",
    pain: "“Am I actually prepared?”",
    heading: "Be more prepared — KPI & audit-ready.",
    body: "A live compliance score, real-time KPIs and one-click, audit-hashed reports mean you’re always ready for an auditor, a grant body or an investor.",
    module: "Reports · Certificate · Trust Center",
  },
  {
    icon: Robot,
    color: "#FFB020",
    pain: "“I’m drowning in admin instead of building my company.”",
    heading: "Solve the real problem — let AI do the busywork.",
    body: "Receipt OCR, bank auto-categorisation and automated reports hand you back the hours you’re losing to bookkeeping, so you can get back to your actual job.",
    module: "AI OCR · Bank import · Advisor",
  },
];

export default function WhyPercen360() {
  return (
    <div className="min-h-screen aurora grain" data-testid="why-page">
      {/* Top bar */}
      <header className="hairline-b sticky top-0 z-30 bg-black/40 backdrop-blur-xl no-print">
        <div className="max-w-[1100px] mx-auto px-6 sm:px-10 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="why-logo-home">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "#FFB020" }}>
              <ShieldCheck size={22} weight="duotone" color="#050B0A" />
            </div>
            <div>
              <div className="font-display leading-none">PerCen360</div>
              <div className="text-[9px] tracking-[0.24em] uppercase text-white/45 mt-1">Why founders switch</div>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <button onClick={() => window.print()} className="btn-ghost text-xs" data-testid="why-print-btn">
              <DownloadSimple size={13} weight="fill" /> Save as PDF
            </button>
            <Link to="/" className="btn-gold text-xs" data-testid="why-cta-top">
              Get started <ArrowRight size={13} weight="bold" />
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-[1100px] mx-auto px-6 sm:px-10 py-16 print-sheet">
        {/* Hero */}
        <section className="max-w-3xl">
          <div className="eyebrow mb-3">Why PerCen360</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
            Founders shouldn’t lose sleep over <span style={{ color: "#FFB020" }}>compliance.</span>
          </h1>
          <p className="text-white/70 text-base leading-relaxed mt-5">
            You started a company to build something — not to chase receipts, decode Revenue rules or
            live in fear of a deadline. Here are the five things keeping founders up at night, and how
            PerCen360 puts each one to rest.
          </p>
        </section>

        {/* Pain points */}
        <section className="mt-14 space-y-6">
          {PAINS.map((p, i) => (
            <motion.div
              key={p.heading}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.4, delay: (i % 2) * 0.05 }}
              className="glass rounded-2xl p-7 sm:p-8 grid md:grid-cols-[auto_1fr] gap-6 items-start"
              data-testid={`pain-${i + 1}`}
            >
              <div className="flex md:flex-col items-center md:items-start gap-3">
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center border border-white/10 bg-black/30"
                >
                  <p.icon size={26} weight="duotone" color={p.color} />
                </div>
                <div className="mono text-3xl text-white/15 hidden md:block">0{i + 1}</div>
              </div>
              <div>
                <div className="text-white/55 text-sm italic">{p.pain}</div>
                <h2 className="font-display text-2xl mt-2 leading-snug">{p.heading}</h2>
                <p className="text-white/70 text-sm leading-relaxed mt-2">{p.body}</p>
                <div className="mt-4 inline-flex items-center gap-2 badge-pass px-3 py-1 rounded-full text-[11px]">
                  <CheckCircle size={13} weight="fill" /> {p.module}
                </div>
              </div>
            </motion.div>
          ))}
        </section>

        {/* CTA */}
        <section className="mt-16 glass rounded-2xl p-10 text-center relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[#FFB020]/8 via-transparent to-transparent pointer-events-none" />
          <div className="relative">
            <div className="eyebrow mb-2">One workspace. Total peace of mind.</div>
            <h2 className="font-display text-3xl sm:text-4xl">Stop worrying. Start building.</h2>
            <p className="text-white/65 text-sm max-w-xl mx-auto mt-3">
              GDPR-native, Revenue.ie rule-checked, audit-ready — for Irish &amp; UK founders and their accountants.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3 mt-7 no-print">
              <Link to="/" className="btn-gold" data-testid="why-cta-bottom">
                Get started <ArrowRight size={14} weight="bold" />
              </Link>
              <Link to="/handout/web" className="btn-ghost" data-testid="why-link-web">Web one-pager</Link>
              <Link to="/handout/mobile" className="btn-ghost" data-testid="why-link-mobile">Mobile one-pager</Link>
            </div>
          </div>
        </section>

        <div className="text-center text-[11px] text-white/45 mt-10">
          percen360.com · GDPR-native · EU region · gillian@percen360.com
        </div>
      </main>
    </div>
  );
}
